import { useMemo, useRef } from 'react';
import CustomButton from '../../buttons/CustomButton';
import { ButtonTypes, Order, PaymentIntent, PaymentResult } from '../../../services/exports/Interfaces';
import { PaymentMethod, PaymentStatus } from '../../../services/exports/Constants';
import { ChildPayButtonProps } from './types';
import SpreedlyChallengeContainer from '../spreedly/SpreedlyChallengeContainer';
import useSpreedlyThreeDs from '../../../hooks/payment/useSpreedlyThreeDs';
import type { CheckoutFunction } from '../../../hooks/payment/useOrderCheckout';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import paymentLog from '../../../services/helpers/PaymentLogger';

/**
 * Spreedly card checkout. Under 3DS2 Global every card charge starts pending:
 * the backend answers 402 with the pending transaction, the 3DS lifecycle
 * device-fingerprints (and challenges when the issuer demands it), and the
 * charge settles synchronously through /spreedly/payment-details — the 402 is
 * the happy path, not an error.
 *
 * Two ways to obtain the payment-method token: a fresh card is tokenized from
 * the card form in the payment-method row; a card on file reuses the saved
 * option's `external_payment_method_id` (the retained Spreedly token) in the
 * same `spreedly_payment_method_token` field — the backend recognises it as
 * the stored credential. Either way the charge is customer-present, so the
 * browser profile is collected and 3DS still runs.
 *
 * Shared by the order and the gift-card pay buttons: `checkout` is whichever
 * the parent runs, so a gift-card purchase never goes through the order path.
 */
export default function SpreedlyPayButton({
  title,
  loading,
  disabled,
  silentlyDisabled,
  setLoading,
  onClick,
  checkout,
  className,
}: ChildPayButtonProps) {
  // The card form renders inside the payment-method row (CardPaymentMethod),
  // consistent with the other gateways; this ref bridges the two columns.
  const { spreedlyCardFormRef, findSavedPaymentMethod, paymentOptions } = usePaymentFlow();
  const { run, cancel, serializeBrowserInfo, challengeActive } = useSpreedlyThreeDs();
  const inFlightRef = useRef(false);

  const savedCard = useMemo(() => findSavedPaymentMethod(PaymentMethod.Card), [paymentOptions]);

  const confirmPayment = async (order: Order, paymentIntent: PaymentIntent): Promise<PaymentResult> => {
    // For Spreedly the 402's payment_intent is the pending transaction
    // (Spreedly's object minus its signed block) — the token is the handle the
    // 3DS lifecycle and the confirm endpoint both key on.
    const transactionToken = (paymentIntent as { token?: string })?.token;

    if (!transactionToken) {
      paymentLog.issue('spreedly', 'payment_intent_missing_token', {
        order_uuid: order?.uuid,
        payment_method: 'card',
      });

      return { status: PaymentStatus.Failed, error: null, result: null };
    }

    return run({ order, transactionToken });
  };

  const _onClick = async () => {
    // With a card on file there is no form to tokenize — the row renders the
    // saved chip instead, so the ref is legitimately empty.
    if ((!savedCard && !spreedlyCardFormRef.current) || inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    // Own the spinner from the first tap. Tokenization is asynchronous and nothing else signals
    // "working" until it resolves — a second tap in that window used to start a second tokenize.
    setLoading(true);

    try {
      let token: string;

      if (savedCard) {
        token = savedCard.external_payment_method_id;
        paymentLog.info('spreedly', 'saved_card_charge', { payment_method: 'card' });
      } else {
        try {
          token = await spreedlyCardFormRef.current.tokenize();
        } catch (error) {
          paymentLog.warn('spreedly', 'tokenize_failed', {
            payment_method: 'card',
            message: error instanceof Error ? error.message : undefined,
          });
          setLoading(false);

          return;
        }
      }

      // Collected before the order exists — Spreedly needs the browser profile at
      // authorization time, and it becomes required the moment the company's
      // gateway carries an SCA provider (harmless before: length-validated only).
      // A saved-card charge is customer-present and runs 3DS too, so it is
      // collected on both paths.
      const browserInfo = serializeBrowserInfo();

      const customCheckout: CheckoutFunction = (confirmFn, paymentIntent, extraData, extraHeaders, options) =>
        checkout(
          confirmFn,
          paymentIntent,
          {
            ...extraData,
            spreedly_payment_method_token: token,
            ...(browserInfo && { spreedly_browser_info: browserInfo }),
          },
          extraHeaders,
          options,
        );

      // From here checkout() owns `loading` — it clears the flag on every exit path.
      await onClick(confirmPayment, undefined, customCheckout);
    } finally {
      inFlightRef.current = false;
    }
  };

  return (
    <>
      <SpreedlyChallengeContainer active={challengeActive} onCancel={cancel} />
      {/* Same markers as PayButton's DefaultButton and StripePayButton: an addressable button plus
          a readiness flag. `silentlyDisabled` never reaches the DOM otherwise, and a click landing
          while it is set is a silent no-op the e2e harness would otherwise race. */}
      <span
        data-test="pay-button-state"
        data-ready={!disabled && !silentlyDisabled && !loading ? 'true' : 'false'}
        className="hidden"
      />
      <CustomButton
        buttonType={ButtonTypes.primary}
        title={title}
        disabled={disabled}
        silentlyDisabled={silentlyDisabled}
        onClick={_onClick}
        loading={loading}
        showArrow
        className={className}
        testId="pay-button"
      />
    </>
  );
}
